import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { TabBar } from '@components/TabBar';
import { useTranslation } from 'react-i18next';
import { allowedScreens } from '@functions/allowed-routes';
import { environment } from 'routes/environment.router';
import { LeadStatusPanel } from './lead-status';
import { DefaultColumnsPanel } from './default-columns';
import { LossReasonsPanel } from './loss-reasons';
import { TagsPanel } from './tags';
import { CustomFieldsPanel } from './custom-fields';

/// O valor vai na URL (?aba=), então fica em português, como os endereços.
type GeneralTab = 'status' | 'colunas' | 'motivos' | 'tags' | 'campos';

/// Cada aba segue sendo uma tela com permissão própria: juntar as cinco não pode dar nem tirar acesso.
const TABS: { value: GeneralTab; label: string; screen: string }[] = [
    { value: 'tags', label: 'sideNav.configuracoes.tags', screen: 'Tags' },
    { value: 'campos', label: 'sideNav.configuracoes.camposPersonalizados', screen: 'CustomFields' },
    { value: 'motivos', label: 'sideNav.configuracoes.motivosPerda', screen: 'LossReasons' },
    { value: 'status', label: 'sideNav.crm.statusLead', screen: 'LeadStatuses' },
    { value: 'colunas', label: 'sideNav.crm.colunasPadrao', screen: 'DefaultColumns' },
];

export const GENERAL_SETTINGS_SCREENS = TABS.map((tab) => tab.screen);

/// Listas cadastradas uma vez e usadas pelo CRM: status, colunas, motivos de perda, tags e campos.
export const GeneralSettings = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const tabs = useMemo(() => {
        const screens = allowedScreens();
        return TABS.filter((tab) => screens.has(tab.screen)
            && (tab.value !== 'campos' || environment.features.customFields));
    }, []);

    const requested = searchParams.get('aba') as GeneralTab | null;
    const current = tabs.find((tab) => tab.value === requested)?.value ?? tabs[0]?.value;

    // A rota só aparece para quem tem ao menos uma das cinco; sem nenhuma, é endereço digitado à mão.
    if (!current) return <Navigate to="/" replace />;

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('gerais.titulo')} />

            {tabs.length > 1 && (
                <Box sx={{ mb: 2.5 }}>
                    <TabBar
                        current={current}
                        onChange={(value) => setSearchParams({ aba: value }, { replace: true })}
                        tabs={tabs.map((tab) => ({ value: tab.value, label: t(tab.label) }))}
                    />
                </Box>
            )}

            {current === 'status' && <LeadStatusPanel />}
            {current === 'colunas' && <DefaultColumnsPanel />}
            {current === 'motivos' && <LossReasonsPanel />}
            {current === 'tags' && <TagsPanel />}
            {current === 'campos' && <CustomFieldsPanel />}
        </DashboardLayout>
    );
};
